/**
 * Messages for a failed project creation.
 *
 * The Dashboard used to render `error.message` straight from the callable.
 * That leaks server wording onto a clinician's screen and, for an unmapped
 * failure, says something meaningless. Everything user-facing is decided here
 * instead, keyed on a machine-readable reason rather than on prose.
 *
 * Pure — no React, no DOM — because `web` has no test runner of its own and
 * must not gain one. Exercised from functions' vitest via `npm run test:web`.
 */

/** The shape a Firebase callable error arrives in, loosely. */
export interface CallableErrorLike {
  code?: unknown;
  message?: unknown;
  details?: unknown;
}

/** Reason the server sends in `details` when the per-account limit is hit. */
export const PROJECT_LIMIT_REASON = "project_limit_reached";

const GENERIC =
  "Could not create the project. Please try again, or contact an administrator if it keeps happening.";

function detailsOf(err: unknown): Record<string, unknown> | null {
  if (!err || typeof err !== "object") return null;
  const d = (err as CallableErrorLike).details;
  return d && typeof d === "object" ? (d as Record<string, unknown>) : null;
}

/** Longest Short Name the server accepts. Mirrors SHORT_NAME_MAX_LENGTH. */
export const SHORT_NAME_MAX_LENGTH = 40;

/**
 * Short Name refusals, keyed on the reason the server sends in `details`.
 *
 * Each one tells the user what to change. A generic "could not create the
 * project" is useless here — the fix is a single field they can see, and every
 * one of these is recoverable by editing it.
 */
const SHORT_NAME_MESSAGES: Record<string, string> = {
  short_name_required:
    "A study Short Name is required. It identifies this study on every " +
    "document the platform produces.",
  short_name_invalid:
    "A study Short Name must contain at least one letter or number. Letters, " +
    "numbers, hyphens and underscores are kept; other characters are removed.",
  short_name_too_long: `A study Short Name may be at most ${SHORT_NAME_MAX_LENGTH} characters.`,
  short_name_unsafe:
    "A study Short Name may not contain slashes or invisible characters. They " +
    "are refused rather than removed, because removing them would change the " +
    "name without telling you.",
  short_name_reserved:
    "That Short Name is reserved. It would appear on every document this study " +
    "produces, where a stand-in cannot be told from a real name.",
  short_name_taken:
    "That Short Name is already used by another of your studies. Short Names " +
    "appear on document filenames, so each study needs its own.",
};

/**
 * Whether this failure is the per-account project-limit refusal.
 *
 * Keyed on `details.reason`, not on the error code or the message: the code
 * arrives with or without a `functions/` prefix depending on the SDK path, and
 * matching prose would break the moment the wording changed.
 */
export function isProjectLimitError(err: unknown): boolean {
  return detailsOf(err)?.reason === PROJECT_LIMIT_REASON;
}

/**
 * The message to show the user. Never returns server prose.
 *
 * When the server reported the limit it is named, so the user knows the number
 * rather than guessing; when it did not, the sentence still reads correctly
 * without it.
 */
export function createProjectErrorMessage(err: unknown): string {
  const shortNameMessage = SHORT_NAME_MESSAGES[String(detailsOf(err)?.reason)];
  if (shortNameMessage) return shortNameMessage;

  if (!isProjectLimitError(err)) return GENERIC;

  const limit = detailsOf(err)?.limit;
  const named = typeof limit === "number" && Number.isFinite(limit);

  return named
    ? `This account has reached its project limit of ${limit}. ` +
        "Contact an administrator to raise it."
    : "This account has reached its project limit. " +
        "Contact an administrator to raise it.";
}
